import { useEffect, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { DecisionPill, ReviewerLine } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { StageRow, stageState } from '~/components/stages';
import { BackHeader, Card, Eyebrow, Notice } from '~/components/ui';
import { useJob } from '~/hooks/useJob';
import type { JobImageOut } from '~/lib/api';
import { api } from '~/lib/api';
import { areaTitle } from '~/lib/areas';
import { logger } from '~/lib/logger';
import { SHOT_LABEL } from '~/lib/photos';
import { useTheme } from '~/theme/ThemeProvider';
import { stageIndexFor, stageTime, timelineFor, timelineLabel } from './timeline';

const KIND_TABS = ['original', 'downscaled', 'inverted', 'contrast', 'edge'] as const;

/** Job detail: timeline, verdict, image variants, enrichment. */
export function JobDetailScreen({
  jobId,
  onBack,
  // The label names where back GOES, and that differs by caller: from the jobs
  // list it returns to the list, from a submit it starts the next inspection.
  backLabel = 'Jobs',
}: {
  jobId: string;
  onBack: () => void;
  backLabel?: string;
}) {
  const { c, t, radius, space } = useTheme();
  const { job, error } = useJob(jobId);
  const [images, setImages] = useState<JobImageOut[]>([]);
  const [kindTab, setKindTab] = useState<(typeof KIND_TABS)[number]>('original');
  /** Which photograph the preview shows; the variant tabs then pick the view of it. */
  const [shotId, setShotId] = useState<string | null>(null);
  const status = job?.status ?? 'queued';

  // Re-fetched on every status change, not once on mount.
  //
  // Arriving here straight from a submit, the job is still queued or analysing and
  // the ONLY image that exists is the original — preprocessing has not written the
  // downscaled/inverted/contrast/edge variants yet. Fetching once meant the
  // variant tabs stayed permanently empty for exactly the jobs the operator had
  // just submitted, while they populated fine for an older job opened from the
  // list. Signed URLs are also short-TTL, so refreshing as the job progresses
  // keeps them live rather than letting them silently expire on screen.
  useEffect(() => {
    let cancelled = false;
    logger.log('[JobDetail] fetching images', { jobId, status });
    api
      .getJobImages(jobId)
      .then((r) => {
        if (!cancelled) setImages(r.images);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [jobId, status]);

  const stages = timelineFor(status, job?.result?.decision_source);
  /*
    Past the end once the job is terminal, so every dot is a tick.

    `stageIndexFor` points at the stage the job is ON, and the waiting screen wants that —
    the job is sitting there. Here the last stage has HAPPENED: the verdict was released.
    Drawing it as the active stage put a live brand dot on a finished job, which reads as
    still running.
  */
  const terminal = status === 'complete' || status === 'partial';
  const stageIndex = terminal ? stages.length : stageIndexFor(status, stages);

  const v = job?.result?.enrichment?.vin?.vehicle;
  const vehicle = v ? `${v.make} ${v.model}` : undefined;
  const reference = job?.result?.claim?.policy_number ?? job?.result?.claim?.assessment_id;

  /*
    One entry per PHOTOGRAPH, taken from the originals: the list carries a row per image
    per variant, so grouping by `job_image_id` is what separates "five views of one shot"
    from "five shots". The preview is that photograph in the chosen variant.
  */
  const shots = images.filter((im) => im.kind === 'original');
  const shot = shots.find((im) => im.job_image_id === shotId) ?? shots[0];
  const preview = shot
    ? (images.find((im) => im.job_image_id === shot.job_image_id && im.kind === kindTab) ?? shot)
    : undefined;

  return (
    <Screen>
      <BackHeader
        title={vehicle ?? `Job ${jobId.slice(0, 8)}`}
        onBack={onBack}
        // The title is the vehicle, so the destination is only spoken, not drawn.
        backLabel={`Back to ${backLabel}`}
        // The claim reference, where the frame puts it: the number quoted on the phone.
        right={
          <Text style={[t.monoValue, { color: c.dim, paddingRight: space.sm }]}>
            {reference ?? jobId.slice(0, 8)}
          </Text>
        }
      />

      <ScrollView
        contentContainerStyle={{
          padding: space.md,
          gap: space.md,
          maxWidth: 720,
          width: '100%',
          alignSelf: 'center',
        }}
      >
        {/*
          The verdict leads, as the frame has it: the decision and who settled it are what
          the screen is opened for, and the timeline below is how it got there.
        */}
        {job?.result ? (
          <Card style={{ gap: space.sm }}>
            <DecisionPill decision={job.result.decision} />
            <ReviewerLine source={job.result.decision_source} />
            <Text style={[t.body, { color: c.text }]}>{job.result.justification}</Text>
            <Text style={[t.monoValue, { color: c.dim }]}>rules {job.result.rules_version}</Text>
            {job.result.blocking_reasons.length > 0 ? (
              <Notice title="Blocked on evidence.">{job.result.blocking_reasons.join(', ')}</Notice>
            ) : null}
          </Card>
        ) : null}

        {/*
          The timeline is vertical and carries the SAME dot vocabulary as the waiting
          screen, so "Processing" looks identical in the two places an operator can
          meet it. `timelineFor` omits the review stage entirely on a job that was never
          held — a stage drawn and never reached reads as one that failed.
        */}
        <Card>
          {stages.map((stage, i) => (
            <StageRow
              key={stage}
              state={stageState(i, stageIndex, stage === 'pending_review')}
              label={timelineLabel(stage)}
              time={stageTime(stage, job?.stages)}
              last={i === stages.length - 1}
            />
          ))}
        </Card>

        {status === 'failed' ? (
          <Notice tone="stateFailed" title="This job failed.">
            {job?.error ?? 'No reason was recorded.'}
          </Notice>
        ) : null}

        {/*
          The photographs, as the frame draws them: one large preview of the shot being
          looked at, the five variants as a segmented control over it, and a strip of the
          job's other shots below. It was a horizontal strip of 160-pt thumbnails with no
          preview at all, which is the wrong way round — the variants exist to be LOOKED
          at, and an edge-detected frame at thumbnail size shows nothing.
        */}
        {preview ? (
          <Card style={{ gap: space.sm }}>
            <Eyebrow>
              {[
                preview.glass_area
                  ? areaTitle(preview.glass_area)
                  : preview.role.replace(/_/g, ' '),
                preview.shot_type ? SHOT_LABEL[preview.shot_type] : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Eyebrow>

            {/*
              A segmented control, not five outline chips: these are five views of ONE
              photograph, so they belong in one track with the chosen one raised out of it.
            */}
            <View
              accessibilityRole="tablist"
              style={{
                flexDirection: 'row',
                gap: space.xxs,
                padding: space.xxs,
                borderRadius: radius.md,
                backgroundColor: c.s3,
              }}
            >
              {KIND_TABS.map((k) => {
                const on = kindTab === k;
                return (
                  <Pressable
                    key={k}
                    testID={`kind-${k}`}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: on }}
                    onPress={() => setKindTab(k)}
                    style={{
                      /*
                        Grow from CONTENT width, not equal fifths. Five equal tabs give
                        each 62 pt and "downscaled" needs 69, so the one word that names
                        the variant an operator is least likely to guess was the one
                        truncated. The five together need 264 of 324, so proportional
                        widths fit them all and still fill the track.
                      */
                      flexGrow: 1,
                      flexBasis: 'auto',
                      paddingHorizontal: space.xs,
                      minHeight: 40,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: radius.sm,
                      backgroundColor: on ? c.surface : 'transparent',
                    }}
                  >
                    <Text
                      numberOfLines={1}
                      style={[t.sub, { fontWeight: '600', color: on ? c.text : c.dim }]}
                    >
                      {k}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Image
              testID="image-preview"
              source={{ uri: preview.url }}
              style={{
                width: '100%',
                aspectRatio: 16 / 10,
                borderRadius: radius.lg,
                backgroundColor: c.s3,
              }}
              resizeMode="cover"
            />

            {shots.length > 1 ? (
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={{ flexDirection: 'row', gap: space.sm }}>
                  {shots.map((im) => {
                    const on = im.job_image_id === preview.job_image_id;
                    return (
                      <Pressable
                        key={im.job_image_id}
                        testID={`shot-${im.job_image_id}`}
                        accessibilityRole="button"
                        accessibilityState={{ selected: on }}
                        accessibilityLabel={
                          im.shot_type ? `${SHOT_LABEL[im.shot_type]} shot` : 'Photograph'
                        }
                        onPress={() => setShotId(im.job_image_id)}
                        style={{ width: 64, height: 48 }}
                      >
                        <Image
                          source={{ uri: im.url }}
                          style={{
                            width: 64,
                            height: 48,
                            borderRadius: radius.md,
                            backgroundColor: c.s3,
                            // Selection is a ring, not an opacity change: alpha is the one
                            // signal direct sun removes entirely.
                            borderWidth: on ? 2 : 0,
                            borderColor: c.text,
                          }}
                          resizeMode="cover"
                        />
                        {im.shot_type ? (
                          <View
                            style={{
                              position: 'absolute',
                              // 4, the frame's inset. At 2 the tag sat on the tile's own
                              // edge and crossed the selection ring.
                              left: space.xs,
                              bottom: space.xs,
                              paddingHorizontal: space.xs,
                              borderRadius: radius.sm,
                              backgroundColor: c.primary,
                            }}
                          >
                            <Text style={[t.micro, { fontWeight: '600', color: c.onPrimary }]}>
                              {SHOT_LABEL[im.shot_type]}
                            </Text>
                          </View>
                        ) : null}
                      </Pressable>
                    );
                  })}
                </View>
              </ScrollView>
            ) : null}
          </Card>
        ) : null}

        {/* enrichment */}
        {job?.result?.enrichment ? (
          <Card style={{ gap: space.xs }}>
            <Eyebrow>enrichment</Eyebrow>
            {job.result.enrichment.vin ? (
              <Text style={[t.body, { color: c.text }]}>
                VIN: {job.result.enrichment.vin.vin_result_state}
                {job.result.enrichment.vin.ocr_vin ? ` · ${job.result.enrichment.vin.ocr_vin}` : ''}
                {job.result.enrichment.vin.vehicle
                  ? ` · ${job.result.enrichment.vin.vehicle.make} ${job.result.enrichment.vin.vehicle.model}`
                  : ''}
                {job.result.enrichment.vin.adas_present ? ' · ADAS' : ''}
              </Text>
            ) : null}
            {job.result.enrichment.glass_type ? (
              <Text style={[t.body, { color: c.text }]}>
                glass: {job.result.enrichment.glass_type.glassType}
                {job.result.enrichment.glass_type.glassManufacturer
                  ? ` (${job.result.enrichment.glass_type.glassManufacturer})`
                  : ''}
              </Text>
            ) : null}
            {job.result.enrichment.warnings?.length ? (
              <Notice title="Enrichment warnings.">
                {job.result.enrichment.warnings.join(', ')}
              </Notice>
            ) : null}
          </Card>
        ) : null}

        {error ? (
          <Notice tone="stateFailed" title="Could not load this job.">
            {error}
          </Notice>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
